import { TimingStatisticsOutput } from "../../../../server/Statistics/OutputsTypes";

export interface TimeData {
  label: string;
  value: number; // en heures
  hours: number;
  minutes: number;
  seconds: number;
}

export interface TimingData {
  daily: TimeData[];
  weekly: TimeData[];
  monthly: TimeData[];
}

// Fonction pour convertir le temps en heures
export const convertTimeToHours = (time: {
  hours: number;
  minutes: number;
  seconds: number;
}) => {
  return time.hours + time.minutes / 60 + time.seconds / 3600;
};

// Fonction pour standardiser les données de temps (convertir secondes en minutes, minutes en heures)
const standardizeTimeData = (timeData: {
  hours: number;
  minutes: number;
  seconds: number;
}) => {
  let { hours, minutes, seconds } = timeData;

  // Convertir les secondes en minutes
  if (seconds >= 60) {
    const additionalMinutes = Math.floor(seconds / 60);
    minutes += additionalMinutes;
    seconds = seconds % 60;
  }

  // Convertir les minutes en heures
  if (minutes >= 60) {
    const additionalHours = Math.floor(minutes / 60);
    hours += additionalHours;
    minutes = minutes % 60;
  }

  return { hours, minutes, seconds };
};

// Mapping des jours anglais vers français
const dayMapping: { [key: string]: string } = {
  Monday: "Lun",
  Tuesday: "Mar",
  Wednesday: "Mer",
  Thursday: "Jeu",
  Friday: "Ven",
  Saturday: "Sam",
  Sunday: "Dim",
};

// Mapping des mois anglais vers français
const monthMapping: { [key: string]: string } = {
  January: "Jan",
  February: "Fév",
  March: "Mar",
  April: "Avr",
  May: "Mai",
  June: "Juin",
  July: "Juil",
  August: "Août",
  September: "Sep",
  October: "Oct",
  November: "Nov",
  December: "Déc",
};

// Fonction pour normaliser et valider les données de temps
const normalizeTimeData = (
  timeData: any,
): { hours: number; minutes: number; seconds: number } => {
  if (!timeData || typeof timeData !== "object") {
    return { hours: 0, minutes: 0, seconds: 0 };
  }

  const rawTime = {
    hours: Number.isFinite(timeData.hours) ? Math.max(0, timeData.hours) : 0,
    minutes: Number.isFinite(timeData.minutes)
      ? Math.max(0, timeData.minutes)
      : 0,
    seconds: Number.isFinite(timeData.seconds)
      ? Math.max(0, timeData.seconds)
      : 0,
  };

  // Standardiser les données de temps
  return standardizeTimeData(rawTime);
};

// Traitement des données journalières
export const processDailyData = (dailyData: any): TimeData[] => {
  console.log("🔍 Traitement des données journalières:", dailyData);

  if (!dailyData || typeof dailyData !== "object") {
    console.log("❌ Aucune donnée journalière valide");
    return [];
  }

  const result: TimeData[] = [];
  const days = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

  // Prioriser les données actuelles sur les données passées
  const currentWeek = dailyData.currentWeek || {};
  const lastWeek = dailyData.lastWeek || {};

  console.log("📅 Semaine actuelle:", currentWeek);
  console.log("📅 Semaine passée:", lastWeek);

  days.forEach((day) => {
    // Chercher la clé correspondante dans le mapping
    const englishDay = Object.keys(dayMapping).find(
      (key) => dayMapping[key] === day,
    );

    if (englishDay) {
      // Chercher dans la semaine actuelle d'abord, puis dans la semaine passée
      const timeData = currentWeek[englishDay] || lastWeek[englishDay];

      if (timeData) {
        const normalizedTime = normalizeTimeData(timeData);
        const hours = convertTimeToHours(normalizedTime);

        result.push({
          label: day,
          value: hours,
          ...normalizedTime,
        });
        console.log(
          `✅ ${day} (${englishDay}): ${normalizedTime.hours}h ${
            normalizedTime.minutes
          }m ${normalizedTime.seconds}s (${hours.toFixed(2)}h total)`,
        );
      } else {
        result.push({
          label: day,
          value: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
        });
        console.log(`⚠️ ${day} (${englishDay}): 0h (pas de données)`);
      }
    } else {
      result.push({
        label: day,
        value: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
      });
      console.log(`❌ ${day}: 0h (mapping non trouvé)`);
    }
  });

  console.log("📊 Résultat final journalier:", result);
  return result;
};

// Traitement des données hebdomadaires
export const processWeeklyData = (weeklyData: any): TimeData[] => {
  console.log("🔍 Traitement des données hebdomadaires:", weeklyData);

  if (!weeklyData || typeof weeklyData !== "object") {
    console.log("❌ Aucune donnée hebdomadaire valide");
    return [];
  }

  const result: TimeData[] = [];

  // Prioriser les données actuelles
  const currentMonth = weeklyData.currentMonth || {};
  const lastMonth = weeklyData.lastMonth || {};

  const allWeeks = { ...currentMonth, ...lastMonth };
  console.log("📅 Toutes les semaines:", allWeeks);

  // Trier les semaines par ordre logique
  const weekEntries = Object.entries(allWeeks)
    .filter(([_, timeData]) => timeData && typeof timeData === "object")
    .sort(([a], [b]) => {
      // Extraire le numéro de semaine
      const weekNumA = parseInt(a.replace(/\D/g, ""));
      const weekNumB = parseInt(b.replace(/\D/g, ""));
      return weekNumA - weekNumB;
    });

  weekEntries.forEach(([weekKey, timeData]: [string, any]) => {
    const normalizedTime = normalizeTimeData(timeData);
    const hours = convertTimeToHours(normalizedTime);

    // Normaliser le label de la semaine
    let label: string;
    if (weekKey.includes(" ")) {
      const parts = weekKey.split(" ");
      label = `Sem ${parts[parts.length - 1]}`;
    } else if (weekKey.startsWith("week")) {
      label = weekKey.replace("week", "Sem ");
    } else {
      label = `Sem ${weekKey}`;
    }

    result.push({
      label,
      value: hours,
      ...normalizedTime,
    });
    console.log(
      `✅ ${weekKey} -> ${label}: ${normalizedTime.hours}h ${
        normalizedTime.minutes
      }m ${normalizedTime.seconds}s (${hours.toFixed(2)}h total)`,
    );
  });

  const finalResult = result.slice(0, 4); // Limiter à 4 semaines
  console.log("📊 Résultat final hebdomadaire:", finalResult);
  return finalResult;
};

// Traitement des données mensuelles
export const processMonthlyData = (monthlyData: any): TimeData[] => {
  console.log("🔍 Traitement des données mensuelles:", monthlyData);

  if (!monthlyData || typeof monthlyData !== "object") {
    console.log("❌ Aucune donnée mensuelle valide");
    return [];
  }

  const result: TimeData[] = [];

  // Prioriser les données actuelles
  const currentYear = monthlyData.currentYear || {};
  const lastYear = monthlyData.lastYear || {};

  const allMonths = { ...currentYear, ...lastYear };
  console.log("📅 Tous les mois:", allMonths);

  // Ordre des mois
  const monthOrder = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Trier les mois par ordre chronologique
  const monthEntries = Object.entries(allMonths)
    .filter(([_, timeData]) => timeData && typeof timeData === "object")
    .sort(([a], [b]) => {
      const indexA = monthOrder.indexOf(a);
      const indexB = monthOrder.indexOf(b);
      return indexA - indexB;
    });

  monthEntries.forEach(([monthKey, timeData]: [string, any]) => {
    const normalizedTime = normalizeTimeData(timeData);
    const hours = convertTimeToHours(normalizedTime);

    // Utiliser le mapping français ou prendre les 3 premières lettres
    const label = monthMapping[monthKey] || monthKey.substring(0, 3);

    result.push({
      label,
      value: hours,
      ...normalizedTime,
    });
    console.log(
      `✅ ${monthKey} -> ${label}: ${normalizedTime.hours}h ${
        normalizedTime.minutes
      }m ${normalizedTime.seconds}s (${hours.toFixed(2)}h total)`,
    );
  });

  const finalResult = result.slice(0, 6); // Limiter à 6 mois
  console.log("📊 Résultat final mensuel:", finalResult);
  return finalResult;
};

// Fonction principale pour normaliser toutes les données
export const normalizeTimingData = (
  rawData: TimingStatisticsOutput,
): TimingData => {
  console.log("🚀 Normalisation des données de timing:", rawData);

  try {
    const daily = processDailyData(rawData.daylyTimeSpent);
    const weekly = processWeeklyData(rawData.weeklyTimeSpent);
    const monthly = processMonthlyData(rawData.monthlyTimeSpent);

    const result = { daily, weekly, monthly };
    console.log("✅ Normalisation terminée:", result);

    return result;
  } catch (error) {
    console.error("❌ Erreur lors de la normalisation:", error);
    return {
      daily: [],
      weekly: [],
      monthly: [],
    };
  }
};
